import React, { useState, useEffect } from 'react';
import { TextField, Checkbox, FormControlLabel, Box, Paper, Typography, Divider, Grid } from '@mui/material';
import { useSelector } from 'react-redux';
import useGetCustomerProfile from '../../../hooks/useGetCustomerProfile';
import { selectRateByShop, selectStandardRate } from '../../../redux/features/todayRateSlice';
import { formatAmountRound } from '../../../utils/formatAmount';
import { generateBlacklistWarningMessage } from '../../../utils/shopNameMapping';
import BlacklistWarningModal from '../../../sharedComponents/BlacklistWarningModal';

const AuctionCalculator = ({ defaultProfile }) => {
  const [customerName, setCustomerName] = useState('');
  const [customerProfile, setCustomerProfile] = useState(null);
  const [blacklistWarningQueue, setBlacklistWarningQueue] = useState([]);
  const [currentBlacklistWarning, setCurrentBlacklistWarning] = useState(null);
  const [rate, setRate] = useState({
    productRate: defaultProfile?.productRate || 0.29,
    transactionFee: defaultProfile?.transactionFee || 0,
  });

  // Options
  const [includeShipping, setIncludeShipping] = useState(false);
  const [includeTax, setIncludeTax] = useState(false);
  const [budgetInTHB, setBudgetInTHB] = useState(false);
  // Tama only: include transaction fee in calculation (default = exclude)
  const [includeTransactionFee, setIncludeTransactionFee] = useState(false);

  // Inputs
  const [productPrice, setProductPrice] = useState('');
  const [domesticShipping, setDomesticShipping] = useState('');
  const [estimatedWeight, setEstimatedWeight] = useState('');

  // Output
  const [result, setResult] = useState('');

  const { getProfileFromContact } = useGetCustomerProfile();
  const shopName = useSelector((state) => state.shop.name);
  const todayRate = useSelector(state => selectRateByShop(state, shopName));
  const standardRate = useSelector(selectStandardRate);

  const handleCloseBlacklistWarning = () => {
    setBlacklistWarningQueue(prev => {
      const remaining = prev.slice(1);
      if (remaining.length > 0) {
        setCurrentBlacklistWarning(remaining[0]);
      } else {
        setCurrentBlacklistWarning(null);
      }
      return remaining;
    });
  };

  // Calculate flow rate (same as AmountCal)
  const calculateFlowRate = (profile) => {
    if (!standardRate || !todayRate) return defaultProfile?.productRate || 0.29;
    const percentageAdjustment = (todayRate / standardRate) - (profile?.extraPercentDiscount || 0);
    const newRate = standardRate * percentageAdjustment - (profile?.extraFlowDiscount || 0);
    return Math.round(newRate * 10000) / 10000;
  };

  // Set default rate when defaultProfile, todayRate, or shopName changes
  useEffect(() => {
    setRate({
      productRate: shopName === "tama" ? todayRate : defaultProfile?.productRate || 0.29,
      transactionFee: shopName === "tama" ? (defaultProfile?.transactionFee || 0) : 0,
    });
  }, [defaultProfile, todayRate, shopName]);

  // Fetch rate when customer name changes
  useEffect(() => {
    const fetchProfile = async () => {
      if (customerName.trim()) {
        const profile = await getProfileFromContact(customerName);
        if (profile) {
          setCustomerProfile(profile);
          let productRate = defaultProfile?.productRate || 0.29;
          let transactionFee = shopName === "tama"
            ? (profile.transactionFee ?? defaultProfile?.transactionFee ?? 0)
            : 0;
          if (profile.isFlowRate) {
            productRate = calculateFlowRate(profile);
          } else if (profile.productRate) {
            productRate = profile.productRate;
          }
          setRate({ productRate, transactionFee });
          
          // Check if blacklisted in other shops
          if (profile.isBlacklistedInOtherShops && profile.blacklistedShops && profile.blacklistedShops.length > 0) {
            const warningMessages = profile.blacklistedShops.map(blacklistedShop => {
              const formattedAmount = formatAmountRound(blacklistedShop.totalPendingAmount);
              return generateBlacklistWarningMessage(blacklistedShop.shop, formattedAmount, blacklistedShop.source);
            });
            setBlacklistWarningQueue(warningMessages);
            if (warningMessages.length > 0) {
              setCurrentBlacklistWarning(warningMessages[0]);
            }
          }
        } else {
          setCustomerProfile(null);
          setRate({
            productRate: shopName === "tama" ? todayRate : defaultProfile?.productRate || 0.29,
            transactionFee: shopName === "tama" ? (defaultProfile?.transactionFee || 0) : 0,
          });
        }
      } else {
        setCustomerProfile(null);
        setRate({
          productRate: shopName === "tama" ? todayRate : defaultProfile?.productRate || 0.29,
          transactionFee: shopName === "tama" ? (defaultProfile?.transactionFee || 0) : 0,
        });
      }
    };
    fetchProfile();
    // eslint-disable-next-line
  }, [customerName, todayRate, standardRate, defaultProfile, shopName]);

  // Calculation logic
  useEffect(() => {
    if (!customerName.trim()) {
      setResult('');
      return;
    }

    let total = Number(productPrice) || 0;
    let deliveryFee = Number(domesticShipping) || 0;
    let rateValue = rate.productRate || 0.29;
    let transactionFeeTHB = Number(rate.transactionFee) || 0;
    let transactionFeeJPY = transactionFeeTHB / rateValue;

    // Subtract deliveryFee only if includeShipping is checked
    if (includeShipping) total -= deliveryFee;
    // Tama only: subtract transactionFee only if "รวมค่ากด" is checked
    if (shopName === 'tama' && includeTransactionFee) total -= transactionFeeJPY;
    // If includeTax, divide by 1.1
    if (includeTax) total = total / 1.1;

    // If budget is in THB, convert to JPY first (before all above)
    if (budgetInTHB) {
      total = Number(productPrice) / rateValue;
      if (includeShipping) total -= deliveryFee;
      if (shopName === 'tama' && includeTransactionFee) total -= transactionFeeJPY;
      if (includeTax) total = total / 1.1;
    }

    total = Math.ceil(total);

    setResult(total && isFinite(total) ? total : '');
  }, [productPrice, domesticShipping, estimatedWeight, includeShipping, includeTax, budgetInTHB, includeTransactionFee, rate, customerName, shopName]);

  return (
    <Box
      width="100%"
      height="100%"
      minWidth={0}
      minHeight={0}
      p={0}
      m={0}
      display="flex"
      justifyContent="center"
      alignItems="flex-start"
      bgcolor="#f5f6fa"
    >
      <Paper
        elevation={4}
        sx={{
          width: '100%',
          height: '100%',
          minWidth: 0,
          minHeight: 0,
          maxWidth: 'none',
          maxHeight: 'none',
          p: 6,
          borderRadius: 4,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="h4" fontWeight="bold" gutterBottom align="center" color="primary" sx={{ fontSize: 32 }}>
          Auction Calculator
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Grid container spacing={6} sx={{ height: '100%' }}>
          {/* Left: Inputs and Options */}
          <Grid item xs={12} md={7} sx={{ height: '100%' }}>
            <Box display="flex" flexDirection="column" gap={3} height="100%" justifyContent="flex-start">
              <TextField
                label="ชื่อลูกค้า"
                value={customerName}
                onChange={e => setCustomerName(e.target.value)}
                size="medium"
                InputLabelProps={{ style: { fontSize: 18 } }}
                inputProps={{ style: { fontSize: 18, height: 24 } }}
              />
              <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2}>
                <FormControlLabel
                  control={<Checkbox checked={includeShipping} onChange={e => setIncludeShipping(e.target.checked)} sx={{ transform: 'scale(1.2)' }} />}
                  label={<span style={{ fontSize: 16 }}>รวมส่ง</span>}
                />
                <FormControlLabel
                  control={<Checkbox checked={includeTax} onChange={e => setIncludeTax(e.target.checked)} sx={{ transform: 'scale(1.2)' }} />}
                  label={<span style={{ fontSize: 16 }}>รวมภาษี</span>}
                />
                <FormControlLabel
                  control={<Checkbox checked={budgetInTHB} onChange={e => setBudgetInTHB(e.target.checked)} sx={{ transform: 'scale(1.2)' }} />}
                  label={<span style={{ fontSize: 16 }}>ให้งบเป็นเงินบาท</span>}
                />
                {shopName === 'tama' && (
                  <FormControlLabel
                    control={<Checkbox checked={includeTransactionFee} onChange={e => setIncludeTransactionFee(e.target.checked)} sx={{ transform: 'scale(1.2)' }} />}
                    label={<span style={{ fontSize: 16 }}>รวมค่าบริการ</span>}
                  />
                )}
              </Box>
              <TextField
                label="ค่าสินค้า"
                value={productPrice}
                onChange={e => setProductPrice(e.target.value)}
                size="medium"
                InputLabelProps={{ style: { fontSize: 18 } }}
                inputProps={{ style: { fontSize: 18, height: 24 } }}
              />
              <TextField
                label="ค่าส่งในญี่ปุ่น"
                value={domesticShipping}
                onChange={e => setDomesticShipping(e.target.value)}
                size="medium"
                InputLabelProps={{ style: { fontSize: 18 } }}
                inputProps={{ style: { fontSize: 18, height: 24 } }}
              />
              {shopName === "tama" && (
                <TextField
                  label="ค่าบริการ (transactionFee)"
                  value={rate.transactionFee}
                  size="medium"
                  InputLabelProps={{ style: { fontSize: 18 } }}
                  inputProps={{ style: { fontSize: 18, height: 24 } }}
                  InputProps={{ readOnly: true }}
                />
              )}
            </Box>
          </Grid>
          {/* Right: Result */}
          <Grid item xs={12} md={5} sx={{ height: '100%' }}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              sx={{ p: 2, border: '1px solid #eee', borderRadius: 2, background: '#fafbfc' }}
            >
              {!customerName.trim() && (productPrice || domesticShipping || estimatedWeight) ? (
                <Typography variant="h6" color="error" sx={{ fontSize: 22, mb: 2 }}>
                  กรุณากรอกชื่อลูกค้าเพื่อคำนวณผลลัพธ์
                </Typography>
              ) : (
                <>
                  <Typography variant="h4" color="secondary" fontWeight="bold" sx={{ fontSize: 32, mb: 2 }}>
                    ผลลัพธ์: {result}
                  </Typography>
                  <Typography variant="h6" color="text.secondary" sx={{ fontSize: 20 }}>
                    เรทที่ใช้: {rate.productRate}
                  </Typography>
                  {shopName === "tama" && (
                    <Typography variant="h6" color="text.secondary" sx={{ fontSize: 20 }}>
                      ค่าบริการ: {rate.transactionFee}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Blacklist Warning Modal */}
      <BlacklistWarningModal
        isOpen={currentBlacklistWarning !== null}
        onClose={handleCloseBlacklistWarning}
        message={currentBlacklistWarning}
      />
    </Box>
  );
};

export default AuctionCalculator;
